import React, { useState, useEffect } from 'react';
import {
  Clock, Plus, Trash2, Play, Zap, Calendar, RotateCcw,
  Pencil, CheckCircle2, Server
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  getSchedules, createSchedule, updateSchedule, deleteSchedule,
  toggleSchedule, runScheduleNow, getEspDevices
} from '@/lib/api';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

// ─── Day Picker ───────────────────────────────────────────────────────────────
const DayPicker = ({ value, onChange }) => (
  <div className="flex gap-1.5 flex-wrap">
    {DAYS.map((day, i) => (
      <button key={i} type="button"
        onClick={() => onChange(value.includes(i) ? value.filter(x => x !== i) : [...value, i])}
        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${value.includes(i) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
        data-testid={`day-pick-${i}`}>{day}
      </button>
    ))}
  </div>
);

// ─── ESP Relay Picker ─────────────────────────────────────────────────────────
// Assignments: [{esp_device_id, relay_numbers}]
const EspRelayPicker = ({ assignments, onChange, espDevices }) => {
  const getSelected = (espId) =>
    assignments.find(a => a.esp_device_id === espId)?.relay_numbers || [];

  const toggle = (espId, num) => {
    const cur = getSelected(espId);
    const updated = cur.includes(num)
      ? cur.filter(n => n !== num)
      : [...cur, num].sort((a, b) => a - b);
    const rest = assignments.filter(a => a.esp_device_id !== espId);
    onChange(updated.length > 0 ? [...rest, { esp_device_id: espId, relay_numbers: updated }] : rest);
  };

  const totalSelected = assignments.reduce((s, a) => s + a.relay_numbers.length, 0);

  // Fallback — hiç ESP cihazı yoksa düz 1-16 grid
  if (espDevices.length === 0) {
    const selected = assignments.flatMap(a => a.relay_numbers);
    const toggleGlobal = (n) => {
      const updated = selected.includes(n)
        ? selected.filter(x => x !== n)
        : [...selected, n].sort((a, b) => a - b);
      onChange(updated.length > 0 ? [{ esp_device_id: 'global', relay_numbers: updated }] : []);
    };
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-8 gap-1.5">
          {Array.from({ length: 16 }, (_, i) => i + 1).map(n => (
            <button key={n} type="button" onClick={() => toggleGlobal(n)}
              className={`h-8 w-8 rounded-lg text-xs font-bold font-mono-tech transition-all ${selected.includes(n) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
              data-testid={`relay-pick-${n}`}>{n}
            </button>
          ))}
        </div>
        {totalSelected > 0 && <p className="text-xs text-muted-foreground">{totalSelected} röle seçili</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3 bg-muted/30 rounded-xl p-3 border border-border">
      {espDevices.map((dev, idx) => {
        const selected = getSelected(dev.id);
        const nums = dev.relay_numbers?.length > 0
          ? dev.relay_numbers
          : Array.from({ length: 16 }, (_, i) => i + 1);
        return (
          <div key={dev.id}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dev.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
              <span className="text-xs font-semibold">{dev.name}</span>
              {dev.zone && <Badge variant="outline" className="text-[10px]">{dev.zone}</Badge>}
              {selected.length > 0 && (
                <span className="text-[10px] text-primary ml-auto font-medium">{selected.length} seçili</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {nums.map(n => (
                <button key={n} type="button" onClick={() => toggle(dev.id, n)}
                  className={`h-8 w-8 rounded-lg text-xs font-bold font-mono-tech transition-all ${selected.includes(n) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  data-testid={`relay-pick-${dev.id}-${n}`}>{n}
                </button>
              ))}
            </div>
            {idx < espDevices.length - 1 && <Separator className="mt-3" />}
          </div>
        );
      })}
      {totalSelected === 0 && (
        <p className="text-xs text-muted-foreground text-center pt-1">Bir veya daha fazla röle seçin</p>
      )}
    </div>
  );
};

// ─── Form Dialog ──────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', relay_assignments: [], action: 'on',
  schedule_type: 'daily', time: '08:00', days: [], date: ''
};

const ScheduleFormDialog = ({ trigger, initial, onSave }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [espDevices, setEspDevices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEspDevices().then(r => setEspDevices(r.data)).catch(() => {});
  }, []);

  const handleOpen = (v) => {
    if (v) {
      if (initial) {
        const assignments = initial.relay_assignments?.length
          ? initial.relay_assignments
          : initial.relay_numbers?.length
            ? [{ esp_device_id: 'global', relay_numbers: initial.relay_numbers }]
            : [];
        setForm({ ...initial, relay_assignments: assignments });
      } else {
        setForm({ ...EMPTY_FORM });
      }
    }
    setOpen(v);
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const totalSelected = form.relay_assignments.reduce((s, a) => s + a.relay_numbers.length, 0);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Zamanlayıcı adı giriniz'); return; }
    if (totalSelected === 0) { toast.error('En az bir röle seçiniz'); return; }
    if (!form.time) { toast.error('Saat giriniz'); return; }
    if (form.schedule_type === 'once' && !form.date) { toast.error('Tarih giriniz'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        relay_assignments: form.relay_assignments,
        relay_numbers: form.relay_assignments.flatMap(a => a.relay_numbers),
        date: form.schedule_type === 'once' ? form.date : null
      });
      setOpen(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid="schedule-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Zamanlayıcıyı Düzenle' : 'Yeni Zamanlayıcı'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">

          <div className="space-y-1.5">
            <Label>Ad *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Akşam Kapatma" data-testid="schedule-name-input" />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Zap size={13} className="text-primary" />
              Röle Seç *
              {totalSelected > 0 && (
                <Badge className="ml-1 text-xs bg-primary/10 text-primary border-primary/30">
                  {totalSelected} seçili
                </Badge>
              )}
            </Label>
            <EspRelayPicker
              assignments={form.relay_assignments}
              onChange={v => set('relay_assignments', v)}
              espDevices={espDevices}
            />
          </div>

          <div className="space-y-1.5">
            <Label>İşlem</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => set('action', 'on')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${form.action === 'on' ? 'bg-green-500 text-white shadow-sm shadow-green-500/30' : 'bg-muted text-muted-foreground'}`}
                data-testid="action-on-btn">Aç (ON)
              </button>
              <button type="button" onClick={() => set('action', 'off')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${form.action === 'off' ? 'bg-destructive text-white shadow-sm' : 'bg-muted text-muted-foreground'}`}
                data-testid="action-off-btn">Kapat (OFF)
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tür</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => set('schedule_type', 'daily')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${form.schedule_type === 'daily' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                data-testid="type-daily-btn">
                <RotateCcw size={14} /> Her Gün
              </button>
              <button type="button" onClick={() => set('schedule_type', 'once')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${form.schedule_type === 'once' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                data-testid="type-once-btn">
                <Calendar size={14} /> Tek Seferlik
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Saat *</Label>
            <Input type="time" value={form.time} onChange={e => set('time', e.target.value)}
              data-testid="schedule-time-input" />
          </div>

          {form.schedule_type === 'daily' && (
            <div className="space-y-1.5">
              <Label>Günler <span className="text-muted-foreground font-normal">(boş = her gün)</span></Label>
              <DayPicker value={form.days} onChange={v => set('days', v)} />
            </div>
          )}
          {form.schedule_type === 'once' && (
            <div className="space-y-1.5">
              <Label>Tarih *</Label>
              <Input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                data-testid="schedule-date-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
            {saving ? 'Kaydediliyor...' : initial ? 'Kaydet' : 'Ekle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Schedule Card ────────────────────────────────────────────────────────────
const ScheduleCard = ({ schedule, onToggle, onDelete, onEdit, onRunNow, espDevices }) => {
  const [running, setRunning] = useState(false);

  const handleRunNow = async () => {
    setRunning(true);
    await onRunNow(schedule.id);
    setRunning(false);
  };

  const formatScheduleInfo = () => {
    if (schedule.schedule_type === 'once') return `${schedule.date} — ${schedule.time}`;
    if (!schedule.days?.length) return `Her gün — ${schedule.time}`;
    return `${schedule.days.map(d => DAYS[d]).join(', ')} — ${schedule.time}`;
  };

  const lastRunDisplay = schedule.last_run
    ? new Date(schedule.last_run).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
    : 'Henüz çalışmadı';

  // Relay badges: from relay_assignments (ESP-aware) or legacy relay_numbers
  const relayBadges = () => {
    if (schedule.relay_assignments?.length) {
      return schedule.relay_assignments.flatMap(a => {
        const dev = espDevices.find(d => d.id === a.esp_device_id);
        return a.relay_numbers.map(n => ({ key: `${a.esp_device_id}-${n}`, label: `${dev?.name ? dev.name.slice(0,4) : ''}·R${n}` }));
      });
    }
    return (schedule.relay_numbers || []).map(n => ({ key: `g-${n}`, label: `R${n}` }));
  };

  const badges = relayBadges();

  return (
    <div
      className={`bg-card border rounded-xl p-4 transition-all duration-200 ${schedule.enabled ? 'border-border hover:border-primary/30' : 'border-border opacity-60'}`}
      data-testid={`schedule-card-${schedule.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold truncate">{schedule.name}</p>
            <Badge className={`text-xs shrink-0 ${schedule.action === 'on' ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30'}`}>
              {schedule.action === 'on' ? 'AÇ' : 'KAPAT'}
            </Badge>
            {schedule.schedule_type === 'once' && (
              <Badge variant="outline" className="text-xs shrink-0">Tek Seferlik</Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
            <Clock size={11} />
            <span>{formatScheduleInfo()}</span>
          </div>

          {/* Relay badges */}
          <div className="flex flex-wrap gap-1 mt-2">
            {badges.slice(0, 6).map(b => (
              <Badge key={b.key} variant="outline" className="text-[10px] font-mono-tech gap-0.5 px-1.5">
                <Zap size={8} />{b.label}
              </Badge>
            ))}
            {badges.length > 6 && (
              <Badge variant="outline" className="text-[10px] px-1.5">+{badges.length - 6}</Badge>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground mt-2">Son çalışma: {lastRunDisplay}</p>
        </div>

        {/* Controls */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Switch checked={schedule.enabled} onCheckedChange={() => onToggle(schedule.id)}
            data-testid={`schedule-toggle-${schedule.id}`} />
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary"
              onClick={handleRunNow} disabled={running} title="Şimdi Çalıştır"
              data-testid={`schedule-run-${schedule.id}`}>
              <Play size={13} className={running ? 'animate-pulse' : ''} />
            </Button>
            <ScheduleFormDialog
              trigger={<Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" data-testid={`schedule-edit-${schedule.id}`}><Pencil size={13} /></Button>}
              initial={schedule}
              onSave={(data) => onEdit(schedule.id, data)}
            />
            <ConfirmDeleteDialog
              testId={`schedule-delete-${schedule.id}`}
              title="Zamanlayıcıyı Sil"
              description={`"${schedule.name}" zamanlayıcısını silmek istediğinizden emin misiniz?`}
              onConfirm={() => onDelete(schedule.id)}
              trigger={
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`schedule-delete-${schedule.id}`}>
                  <Trash2 size={13} />
                </Button>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [espDevices, setEspDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getSchedules(), getEspDevices()])
      .then(([s, e]) => { setSchedules(s.data); setEspDevices(e.data); })
      .catch(() => toast.error('Veriler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (data) => {
    try {
      const res = await createSchedule(data);
      setSchedules(prev => [...prev, res.data]);
      toast.success('Zamanlayıcı eklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zamanlayıcı eklenemedi');
      throw err;
    }
  };

  const handleEdit = async (id, data) => {
    try {
      const res = await updateSchedule(id, data);
      setSchedules(prev => prev.map(s => s.id === id ? res.data : s));
      toast.success('Zamanlayıcı güncellendi');
    } catch { toast.error('Güncellenemedi'); throw new Error(); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSchedule(id);
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Zamanlayıcı silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleSchedule(id);
      setSchedules(prev => prev.map(s => s.id === id ? { ...s, enabled: res.data.enabled } : s));
    } catch { toast.error('Durum güncellenemedi'); }
  };

  const handleRunNow = async (id) => {
    try {
      const res = await runScheduleNow(id);
      const s = schedules.find(x => x.id === id);
      toast.success(`"${s?.name}" çalıştırıldı → ${res.data.relay_numbers.join(',')} ${res.data.action.toUpperCase()}`);
      setSchedules(prev => prev.map(x => x.id === id ? { ...x, last_run: new Date().toISOString() } : x));
    } catch { toast.error('Zamanlayıcı çalıştırılamadı'); }
  };

  const activeCount = schedules.filter(s => s.enabled).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Zamanlayıcı</h1>
          <p className="text-muted-foreground text-sm mt-1">Rölelerin otomatik açma/kapama zamanlaması</p>
        </div>
        <div className="flex items-center gap-3">
          {schedules.length > 0 && (
            <Badge className={`gap-1.5 px-3 py-1.5 ${activeCount > 0 ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground'}`}>
              <CheckCircle2 size={13} /> {activeCount} Aktif
            </Badge>
          )}
          <ScheduleFormDialog
            trigger={<Button className="gap-2" data-testid="add-schedule-btn"><Plus size={16} /> Zamanlayıcı Ekle</Button>}
            onSave={handleCreate}
          />
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
        <Clock size={16} className="text-primary mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-medium text-foreground">Zamanlayıcı her 30 saniyede kontrol eder</p>
          <p className="text-muted-foreground text-xs mt-0.5">Ayarlar → Saat Dilimi'nden UTC ofsetini doğru ayarlayın.</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="empty-schedules">
          <Clock size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground font-medium">Henüz zamanlayıcı eklenmemiş</p>
          <p className="text-sm text-muted-foreground mt-1">Örnek: "Akşam 20:00'da ofis ışıklarını kapat"</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="schedules-list">
          {schedules.map(s => (
            <ScheduleCard
              key={s.id}
              schedule={s}
              espDevices={espDevices}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onRunNow={handleRunNow}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Schedules;
